/**
 * Shared, framework-agnostic helpers for the PayZen form-token flow.
 *
 * Kept free of React / Next imports so it can be unit-tested with `node --test`
 * (the repo's existing test convention) and reused by both the API route
 * (`pages/api/payzen/form-token.ts`) and the checkout components.
 *
 * NOTE: these helpers never touch the PayZen request body (amount, currency,
 * customer, orderId) and never log the formToken.
 */

/** Hard ceiling for the server->PayZen request so the endpoint fails fast
 *  instead of hanging a client loader forever. */
export const PAYZEN_REQUEST_TIMEOUT_MS = 12000;

/** Typed error so the route can map a cause to an HTTP status + stable code. */
export class FormTokenError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'FormTokenError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Map any error thrown while creating a form token to a stable
 * { status, code, message } triple for the HTTP response.
 * Never leaks internal details or tokens.
 */
export function classifyFormTokenError(err: unknown): {
  status: number;
  code: string;
  message: string;
} {
  if (err instanceof FormTokenError) {
    return { status: err.status, code: err.code, message: err.message };
  }
  const name = (err as { name?: string } | null)?.name;
  // AbortSignal.timeout() rejects with a TimeoutError; manual abort -> AbortError.
  if (name === 'TimeoutError' || name === 'AbortError') {
    return {
      status: 504,
      code: 'PAYMENT_PROVIDER_TIMEOUT',
      message: 'El proveedor de pago no respondió a tiempo.',
    };
  }
  const msg = (err as { message?: string } | null)?.message;
  if (
    msg === 'Event does not exist' ||
    msg === 'Entrance does not belong to this event' ||
    msg === 'Entrance is not available'
  ) {
    return { status: 400, code: 'INVALID_EVENT', message: msg };
  }
  return {
    status: 502,
    code: 'FORM_TOKEN_FAILED',
    message: 'No se pudo generar el formulario de pago.',
  };
}

/* -------------------------------------------------------------------------- */
/* Client side: interpret the /api/payzen/form-token response.                */
/* -------------------------------------------------------------------------- */

export type FormTokenOutcome = {
  formToken: string | null;
  error: { code: string; message: string } | null;
};

const GENERIC_TOKEN_ERROR = 'No se pudo generar el formulario de pago. Intenta de nuevo.';

/** Pure mapping from an HTTP result to a usable outcome. */
export function interpretFormTokenResponse(
  ok: boolean,
  body: unknown
): FormTokenOutcome {
  const token = (body as { formToken?: unknown } | null)?.formToken;
  if (ok && typeof token === 'string' && token.length > 0) {
    return { formToken: token, error: null };
  }
  const b = body as { code?: string; error?: string } | null;
  return {
    formToken: null,
    error: { code: b?.code || 'FORM_TOKEN_FAILED', message: b?.error || GENERIC_TOKEN_ERROR },
  };
}

/**
 * Request a form token. Reuses the caller-provided `orderId` inside `payload`
 * (does NOT create a new order/ticket). Never throws: a network failure maps to
 * a NETWORK outcome so the UI can show an actionable error instead of hanging.
 */
export async function requestFormToken(
  fetchImpl: typeof fetch,
  payload: unknown
): Promise<FormTokenOutcome> {
  try {
    const response = await fetchImpl('/api/payzen/form-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    return interpretFormTokenResponse(response.ok, body);
  } catch {
    return {
      formToken: null,
      error: {
        code: 'NETWORK',
        message:
          'No pudimos conectar con el proveedor de pago. Revisa tu conexión e intenta de nuevo.',
      },
    };
  }
}
